import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Youtube, Twitter, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, ThemeToggle } from "@/components/app/common";
import { useAuth } from "@/context/AuthContext";

export const PublicHeader = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  const links = [["/#ozellikler", "Özellikler"], ["/#nasil", "Nasıl Çalışır"], ["/#paketler", "Paketler"], ["/#sss", "SSS"]];
  return (
    <header className="glass fixed inset-x-0 top-0 z-50 border-x-0 border-t-0">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8">
        <Logo />
        <nav className="hidden items-center gap-7 text-sm font-medium text-muted-foreground md:flex">
          {links.map(([h, l]) => <a key={h} href={h} className="transition-colors hover:text-foreground">{l}</a>)}
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {user ? (
            <Button asChild className="btn-glow rounded-full" data-testid="header-panel-button"><Link to="/panel">Panelim</Link></Button>
          ) : (
            <>
              <Button asChild variant="ghost" className="hidden sm:inline-flex" data-testid="header-login-button"><Link to="/giris">Giriş Yap</Link></Button>
              <Button asChild className="btn-glow hidden rounded-full sm:inline-flex" data-testid="header-register-button"><Link to="/kayit">Hemen Başla</Link></Button>
            </>
          )}
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(!open)} data-testid="public-mobile-menu">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-border px-4 py-4 md:hidden">
          {links.map(([h, l]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2 text-sm">{l}</a>)}
          {!user && <Link to="/giris" className="block py-2 text-sm">Giriş Yap</Link>}
          {!user && <Link to="/kayit" className="block py-2 text-sm font-semibold text-primary" data-testid="mobile-register-link">Hemen Başla</Link>}
        </div>
      )}
    </header>
  );
};

const SOCIAL = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin, youtube: Youtube, x: Twitter };

export const Footer = () => {
  const { settings } = useAuth();
  const legal = [["kvkk", "KVKK Aydınlatma Metni"], ["acik-riza", "Açık Rıza Metni"], ["gizlilik", "Gizlilik Politikası"], ["kullanim-kosullari", "Kullanım Koşulları"],
    ["kullanici-sozlesmesi", "Kullanıcı Sözleşmesi"], ["mesafeli-satis", "Mesafeli Satış Sözleşmesi"], ["iptal-iade", "İptal / İade Politikası"], ["cerez", "Çerez Politikası"]];
  return (
    <footer className="border-t border-border bg-card/40">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-8 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo />
          <p className="mt-4 max-w-sm text-sm text-muted-foreground">{settings.tagline || "Yapay zekâ destekli profesyonel içerik stüdyosu."}</p>
          <div className="mt-5 flex gap-2">
            {Object.entries(settings.social || {}).filter(([, v]) => v).map(([k, v]) => {
              const I = SOCIAL[k] || Instagram;
              return <a key={k} href={v} target="_blank" rel="noopener noreferrer" data-testid={`social-${k}`} className="grid h-9 w-9 place-items-center rounded-full border border-border transition-colors hover:border-primary hover:text-primary"><I className="h-4 w-4" /></a>;
            })}
          </div>
        </div>
        <div>
          <div className="eyebrow mb-4">Yasal</div>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {legal.map(([s, l]) => <li key={s}><Link to={`/yasal/${s}`} data-testid={`footer-legal-${s}`} className="hover:text-foreground">{l}</Link></li>)}
          </ul>
        </div>
        <div>
          <div className="eyebrow mb-4">İletişim</div>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {settings.contact_email && <li>{settings.contact_email}</li>}
            {settings.contact_phone && <li>{settings.contact_phone}</li>}
            <li><Link to="/iletisim" data-testid="footer-contact-link" className="hover:text-foreground">İletişim Sayfası</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border py-6 text-center text-xs text-muted-foreground">© {new Date().getFullYear()} {settings.company_name && !settings.company_name.startsWith("[") ? settings.company_name : settings.site_name}. Tüm hakları saklıdır.</div>
    </footer>
  );
};

export const CookieConsent = () => {
  const [show, setShow] = useState(() => !localStorage.getItem("cookie_consent"));
  if (!show) return null;
  const save = (v) => { localStorage.setItem("cookie_consent", JSON.stringify({ value: v, at: new Date().toISOString() })); setShow(false); };
  return (
    <div className="glass fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-2xl rounded-2xl p-5 shadow-2xl sm:inset-x-6" data-testid="cookie-consent">
      <p className="text-sm text-muted-foreground">
        Deneyiminizi iyileştirmek için zorunlu çerezler kullanıyoruz. Analitik çerezler yalnızca onayınızla kullanılır. Ayrıntılar için <Link to="/yasal/cerez" className="text-primary underline">Çerez Politikası</Link>.
      </p>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => save("essential")} data-testid="cookie-essential-button">Sadece Zorunlu</Button>
        <Button size="sm" className="btn-glow" onClick={() => save("all")} data-testid="cookie-accept-button">Tümünü Kabul Et</Button>
      </div>
    </div>
  );
};

export const PublicLayout = ({ children }) => (
  <div className="min-h-screen"><PublicHeader /><main className="pt-16">{children}</main><Footer /></div>
);
